import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { heroSlides } from '../mockData';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import DestinationSelector from './DestinationSelector';
import { useTranslation } from 'react-i18next';

const HeroSlider = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const slidesLength = heroSlides.length;

  // Handler for main CTA button
  const handleMainCTA = (slideId) => {
    if (slideId === 1) {
      // Award slide - scroll to Why Choose Us
      window.scrollTo({ top: 2600, behavior: 'smooth' });
    } else if (slideId === 2) {
      // US slide
      navigate('/services/usa-urgent-visa');
    } else if (slideId === 3) {
      // Schengen slide
      navigate('/services/urgent-schengen-visa');
    } else if (slideId === 4) {
      // Japan slide
      navigate('/services/japan-visa');
    }
  };

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slidesLength);
  }, [slidesLength, setCurrentSlide]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slidesLength) % slidesLength);
  }, [slidesLength, setCurrentSlide]);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
  }, [setCurrentSlide]);

  useEffect(() => {
    const timer = setInterval(() => {
      nextSlide();
    }, 5000);

    return () => clearInterval(timer);
  }, [nextSlide]);

  const slide = heroSlides[currentSlide];
  
  const getTranslatedContent = () => {
    switch(slide.id) {
      case 1:
        return {
          title: t('heroAward'),
          subtitle: t('heroAwardSubtitle'),
          cta: t('heroAwardCta')
        };
      case 2:
        return {
          title: t('heroUs'),
          subtitle: t('heroUsSubtitle'),
          cta: t('heroUsCta')
        };
      case 3:
        return {
          title: t('heroSchengen'),
          subtitle: t('heroSchengenSubtitle'),
          cta: t('heroSchengenCta')
        };
      case 4:
        return {
          title: t('heroJapan'),
          subtitle: t('heroJapanSubtitle'),
          cta: t('heroJapanCta')
        };
      default:
        return slide;
    }
  };

  const content = getTranslatedContent();

  return (
    <section className="relative min-h-[700px] flex items-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 transition-opacity duration-1000">
        <img
          src={slide.backgroundImage}
          alt={slide.country}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/30"></div>
        
        {/* Bottom gradient for smooth transition */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white via-white/80 to-transparent"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Left Content */}
          <div className="space-y-6 animate-fadeIn">
            <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight drop-shadow-2xl">
              {content.title}
            </h1>
            <p className="text-xl text-white/90 drop-shadow-lg">{content.subtitle}</p>
            <div className="flex flex-wrap gap-4">
              <button 
                onClick={() => handleMainCTA(slide.id)}
                className="bg-blue-700 text-white px-8 py-4 rounded-full font-semibold hover:bg-teal-800 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 cursor-pointer"
              >
                {content.cta}
              </button>
              <a
                href="https://wa.me/971585862103?text=Hi, I need a free consultation for visa services"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-logo-blue to-logo-cyan text-white px-8 py-4 rounded-full font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 inline-flex items-center"
              >
                {t('getConsultation')}
              </a>
            </div>
          </div>

          {/* Right Destination Selector */}
          <div className="lg:ml-auto w-full max-w-md">
            <DestinationSelector />
          </div>
        </div>
      </div>

      {/* Slider Controls */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex items-center space-x-4 z-10">
        <button
          onClick={prevSlide}
          className="p-2 rounded-full bg-white/80 hover:bg-white transition-colors shadow-md"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-6 h-6 text-purple-900" />
        </button>

        <div className="flex space-x-2">
          {heroSlides.map((slide, index) => (
            <button
              key={slide.id}
              onClick={() => goToSlide(index)}
              className={`h-3 rounded-full transition-all ${
                index === currentSlide ? 'bg-orange-500 w-8' : 'bg-white/60 hover:bg-white/80 w-3'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        <button
          onClick={nextSlide}
          className="p-2 rounded-full bg-white/80 hover:bg-white transition-colors shadow-md"
          aria-label="Next slide"
        >
          <ChevronRight className="w-6 h-6 text-purple-900" />
        </button>
      </div>
    </section>
  );
};

export default HeroSlider;